import { useEffect } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import {
  UpsertTemplateResult,
  EmbeddedContentArgsInput,
  InvalidTemplateContents,
} from 'modules/graphql/types';
import { addNegativeToast } from 'modules/toastContainer';
import { isEmpty } from '@iterable/utils';
import noOp from 'utils/noop';
import {
  GqlInvalidContentReason,
  GqlUpsertTemplateFailureTypes,
  GqlUpsertTemplateInvalidContentFailureTypes,
} from 'modules/templateEditor/common';
import { getErrorMessage } from '../../common/utils';

import { Config } from './types';

export type UpdateEmbeddedTemplateRequest = {
  templateId: number;
  campaignId?: number;
  name: string;
  messageTypeId: number;
  content: EmbeddedContentArgsInput;
  cacheDataFeed: boolean;
  dataFeedIds: [number];
  mergeDataFeedContext: boolean;
  referenceDataFeedsByAlias: boolean;
  destinationFolderId?: number;
};

type UpdateEmbeddedTemplateResponse = {
  updateEmbeddedTemplate: UpsertTemplateResult;
};

export const UpdateEmbeddedTemplateMutation = gql`
  mutation UpdateEmbeddedTemplate(
    $cacheDataFeed: Boolean!
    $campaignId: Long
    $content: EmbeddedContentArgsInput!
    $dataFeedIds: [Long!]!
    $mergeDataFeedContext: Boolean!
    $messageTypeId: Long!
    $name: String!
    $referenceDataFeedsByAlias: Boolean!
    $templateId: Long!
  ) {
    updateEmbeddedTemplate(
      cacheDataFeed: $cacheDataFeed
      campaignId: $campaignId
      content: $content
      dataFeedIds: $dataFeedIds
      mergeDataFeedContext: $mergeDataFeedContext
      messageTypeId: $messageTypeId
      name: $name
      referenceDataFeedsByAlias: $referenceDataFeedsByAlias
      templateId: $templateId
    ) {
      failure {
        ... on InvalidTemplateContents {
          errors {
            ... on InvalidTemplateContent {
              reasons {
                ... on Unrenderable {
                  fieldName
                  msg
                }
                ... on MissingSnippets {
                  snippets
                }
                ... on InvalidSnippetParams {
                  snippetsWithParams
                }
              }
              content {
                ... on EmbeddedContentArgs {
                  locale {
                    name
                  }
                }
              }
            }
          }
        }
        ... on TemplateNotFound {
          templateId
        }
        __typename
      }
      templateId
    }
  }
`;

export const useUpdateEmbeddedTemplate = ({
  onComplete = noOp,
  onError = noOp,
  onSuccess = noOp,
}: Config) => {
  const dispatch = useDispatch();
  const [upsertEmbeddedTemplate, data] = useMutation<
    UpdateEmbeddedTemplateResponse,
    UpdateEmbeddedTemplateRequest
  >(UpdateEmbeddedTemplateMutation);
  const fieldErrors: Record<string, string> = {};

  useEffect(() => {
    if (!data.called) return;
    if (!data.loading && data.error) {
      dispatch(addNegativeToast('An unknown error occurred'));
    }

    if (!data.loading && data?.data?.updateEmbeddedTemplate) {
      const { failure, templateId } = data.data.updateEmbeddedTemplate;
      const failures = (Array.isArray(failure) || failure === null ? failure : [failure]) ?? [];
      const errorMessages: string[] = [];
      failures.forEach((error) => {
        if (error.__typename === GqlUpsertTemplateFailureTypes.INVALID_TEMPLATE_CONTENTS) {
          (error as InvalidTemplateContents).errors.forEach((invalidContentError) =>
            invalidContentError.reasons.forEach((reason) => {
              const invalidContentReason = reason as GqlInvalidContentReason;
              if (
                invalidContentReason.__typename ===
                  GqlUpsertTemplateInvalidContentFailureTypes.ILLEGAL_SCRIPT ||
                invalidContentReason.__typename ===
                  GqlUpsertTemplateInvalidContentFailureTypes.JS_IN_ANCHOR_TAG ||
                invalidContentReason.__typename ===
                  GqlUpsertTemplateInvalidContentFailureTypes.JS_IN_IFRAME_TAG
              )
                errorMessages.push(getErrorMessage(invalidContentReason));
            }),
          );
        } else {
          errorMessages.push(getErrorMessage(error));
        }
      });

      errorMessages.forEach((errorMessage) => dispatch(addNegativeToast(errorMessage)));

      if (templateId) {
        onSuccess(templateId);
      } else if (!isEmpty(fieldErrors)) {
        onError(fieldErrors);
      }
    }
    onComplete();
  }, [data.loading]);

  return [upsertEmbeddedTemplate, data] as const;
};
